import React, { useState } from 'react';
import './TwoFactorSetup.css';
import { useAuth } from '../hooks/useAuth';

const TwoFactorSetup = ({ isOpen, onClose, onComplete }) => {
  const [step, setStep] = useState(1); // 1: setup, 2: verify
  const [verificationCode, setVerificationCode] = useState('');
  const [sentCode, setSentCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const { user } = useAuth();

  // Validate email domain
  const isValidEmailDomain = (email) => {
    return email && email.toLowerCase().endsWith('@relishiq.com');
  };

  // Generate a 6-digit verification code
  const generateVerificationCode = () => {
    return Math.floor(100000 + Math.random() * 900000).toString();
  };

  // Simulate sending email (in production, this would be a real email service)
  const sendVerificationEmail = async () => {
    if (!isValidEmailDomain(user?.email)) {
      setError('2FA is only available for @relishiq.com email addresses.');
      return false;
    }

    setIsLoading(true);
    setError('');

    try {
      // Generate verification code
      const code = generateVerificationCode();
      setSentCode(code);

      // Simulate email sending delay
      await new Promise(resolve => setTimeout(resolve, 2000));

      // In production, you would send this via your backend:
      // await fetch('/api/send-2fa-email', {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify({ email: user.email, code })
      // });

      console.log(`🔐 2FA Code sent to ${user.email}: ${code}`);
      setStep(2);
      return true;
    } catch (err) {
      setError('Failed to send verification email. Please try again.');
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  // Verify the entered code
  const verifyCode = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      setError('Please enter a valid 6-digit code.');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      // Simulate verification delay
      await new Promise(resolve => setTimeout(resolve, 1000));

      if (verificationCode === sentCode) {
        // Save 2FA status
        localStorage.setItem('2fa_enabled', 'true');
        localStorage.setItem('2fa_email', user.email);
        
        onComplete && onComplete();
        onClose();
      } else {
        setError('Invalid verification code. Please try again.');
      }
    } catch (err) {
      setError('Verification failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setStep(1);
    setVerificationCode('');
    setSentCode('');
    setError('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="twofa-modal">
        <div className="twofa-header">
          <h2>Two-Factor Authentication</h2>
          <button className="close-button" onClick={handleClose}>×</button>
        </div>

        {step === 1 && (
          <div className="twofa-content">
            <div className="twofa-info">
              <div className="security-icon">
                <svg viewBox="0 0 24 24" fill="currentColor" className="security-shield-icon">
                  <path d="M12,1L3,5V11C3,16.55 6.84,21.74 12,23C17.16,21.74 21,16.55 21,11V5L12,1M12,7C13.4,7 14.8,8.6 14.8,10.5V11.5C15.4,11.5 16,12.4 16,13V16C16,16.6 15.6,17 15,17H9C8.4,17 8,16.6 8,16V13C8,12.4 8.4,11.5 9,11.5V10.5C9,8.6 10.6,7 12,7M12,8.2C11.2,8.2 10.2,8.7 10.2,10.5V11.5H13.8V10.5C13.8,8.7 12.8,8.2 12,8.2Z" />
                </svg>
              </div>
              <h3>Secure Your Account</h3>
              <p>Add an extra layer of security to your CSM Dashboard account.</p>
              
              <div className="email-info">
                <strong>Verification Email:</strong>
                <div className="email-display">{user?.email}</div>
                {!isValidEmailDomain(user?.email) && (
                  <div className="error-text">
                    <svg className="warning-icon" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    Only @relishiq.com email addresses can enable 2FA
                  </div>
                )}
              </div>

              <div className="benefits-list">
                <h4>Benefits:</h4>
                <ul>
                  <li><span className="benefit-icon">•</span> Enhanced account security</li>
                  <li><span className="benefit-icon">•</span> Protection against unauthorized access</li>
                  <li><span className="benefit-icon">•</span> Email-based verification codes</li>
                  <li><span className="benefit-icon">•</span> Easy setup and management</li>
                </ul>
              </div>
            </div>

            {error && <div className="error-message">{error}</div>}

            <div className="twofa-actions">
              <button 
                className="btn-secondary" 
                onClick={handleClose}
                disabled={isLoading}
              >
                Cancel
              </button>
              <button 
                className="btn-primary" 
                onClick={sendVerificationEmail}
                disabled={isLoading || !isValidEmailDomain(user?.email)}
              >
                {isLoading ? 'Sending...' : 'Send Verification Code'}
              </button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="twofa-content">
            <div className="verification-step">
              <div className="email-sent-icon">
                <svg viewBox="0 0 24 24" fill="currentColor" className="email-icon">
                  <path d="M20,8L12,13L4,8V6L12,11L20,6M20,4H4C2.89,4 2,4.89 2,6V18A2,2 0 0,0 4,20H20A2,2 0 0,0 22,18V6C22,4.89 21.1,4 20,4Z" />
                </svg>
              </div>
              <h3>Check Your Email</h3>
              <p>We've sent a 6-digit verification code to:</p>
              <div className="email-display">{user?.email}</div>
              
              <div className="code-input-section">
                <label htmlFor="verification-code">Enter Verification Code:</label>
                <input
                  id="verification-code"
                  type="text"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  className="code-input"
                  maxLength={6}
                  autoComplete="off"
                />
              </div>

              {error && <div className="error-message">{error}</div>}

              <div className="helper-text">
                <p>Didn't receive the code? Check your spam folder or try again.</p>
              </div>
            </div>

            <div className="twofa-actions">
              <button 
                className="btn-secondary" 
                onClick={() => setStep(1)}
                disabled={isLoading}
              >
                Back
              </button>
              <button 
                className="btn-primary" 
                onClick={verifyCode}
                disabled={isLoading || verificationCode.length !== 6}
              >
                {isLoading ? 'Verifying...' : 'Verify & Enable 2FA'}
              </button>
            </div>
          </div>
        )}

        {/* Development helper - remove in production */}
        {step === 2 && sentCode && (
          <div className="dev-helper">
            <small>Dev Mode: Code is {sentCode}</small>
          </div>
        )}
      </div>
    </div>
  );
};

export default TwoFactorSetup;